import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { UserPlus, X, Filter, CheckCircle2, Trash2, Search, Download, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

function formatDate(iso) {
    if (!iso) return "";
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function Badge({ tone = "muted", children }) {
    const map = {
        success: "bg-green-100 text-green-700",
        warning: "bg-orange-100 text-brand-orange",
        muted: "bg-gray-100 text-gray-600",
        info: "bg-blue-100 text-blue-700",
    };
    return <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${map[tone]}`}>{children}</span>;
}

function EnrollDialog({ open, onClose, onCreated, presetUserId }) {
    const [stagiaires, setStagiaires] = useState([]);
    const [sessions, setSessions] = useState([]);
    const [form, setForm] = useState({ user_id: presetUserId || "", session_id: "", payment_status: "manual", notes: "" });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!open) return;
        Promise.all([
            api.get("/admin/users", { params: { role: "stagiaire" } }),
            api.get("/sessions"),
        ])
            .then(([u, s]) => {
                setStagiaires(u.data);
                const today = new Date().toISOString().slice(0, 10);
                setSessions(s.data.filter((x) => x.start_date >= today && x.available_seats > 0));
            })
            .catch((err) => toast.error(formatApiError(err)));
    }, [open]);

    useEffect(() => {
        if (presetUserId) setForm((f) => ({ ...f, user_id: presetUserId }));
    }, [presetUserId]);

    const submit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post("/admin/enrollments", {
                user_id: form.user_id,
                session_id: form.session_id,
                payment_status: form.payment_status,
                notes: form.notes || null,
            });
            toast.success("Inscription créée");
            onCreated();
            onClose();
            setForm({ user_id: "", session_id: "", payment_status: "manual", notes: "" });
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;
    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-ink/60 p-4" data-testid="enroll-dialog">
            <motion.div
                initial={{ opacity: 0, scale: 0.96, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                className="relative w-full max-w-xl rounded-3xl bg-white p-8 shadow-premium-lg"
            >
                <button onClick={onClose} className="absolute right-5 top-5 text-brand-smoke hover:text-brand-ink">
                    <X className="h-5 w-5" />
                </button>
                <h3 className="font-display text-3xl">Inscrire un stagiaire</h3>
                <p className="mt-2 text-sm text-brand-smoke">Sélectionnez un stagiaire et une session disponible.</p>

                <form onSubmit={submit} className="mt-6 space-y-4">
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Stagiaire *</label>
                        <select
                            required
                            value={form.user_id}
                            onChange={(e) => setForm((f) => ({ ...f, user_id: e.target.value }))}
                            disabled={!!presetUserId}
                            data-testid="enroll-user-select"
                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange disabled:opacity-70"
                        >
                            <option value="">— Sélectionner —</option>
                            {stagiaires.map((u) => (
                                <option key={u.id} value={u.id}>{u.first_name} {u.last_name} ({u.email})</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Session *</label>
                        <select
                            required
                            value={form.session_id}
                            onChange={(e) => setForm((f) => ({ ...f, session_id: e.target.value }))}
                            data-testid="enroll-session-select"
                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange"
                        >
                            <option value="">— Sélectionner —</option>
                            {sessions.map((s) => (
                                <option key={s.id} value={s.id}>
                                    {s.formation_name} — {formatDate(s.start_date)} — {s.city} ({s.available_seats} pl.)
                                </option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Statut paiement</label>
                        <div className="mt-2 grid grid-cols-2 gap-3">
                            {[
                                { v: "manual", label: "Payé (manuel)" },
                                { v: "pending", label: "En attente" },
                            ].map((o) => (
                                <label
                                    key={o.v}
                                    className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm ${form.payment_status === o.v ? "border-brand-orange bg-brand-orange/5" : "border-brand-line"}`}
                                >
                                    <input
                                        type="radio"
                                        name="payment_status"
                                        value={o.v}
                                        checked={form.payment_status === o.v}
                                        onChange={(e) => setForm((f) => ({ ...f, payment_status: e.target.value }))}
                                        data-testid={`enroll-payment-${o.v}`}
                                        className="h-4 w-4 accent-brand-orange"
                                    />
                                    {o.label}
                                </label>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-semibold uppercase tracking-wider">Notes</label>
                        <textarea
                            rows={3}
                            value={form.notes}
                            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                            data-testid="enroll-notes"
                            className="mt-2 w-full rounded-xl border border-brand-line bg-white px-4 py-3 outline-none focus:border-brand-orange"
                        />
                    </div>
                    <div className="flex items-center justify-end gap-3 pt-3">
                        <button type="button" onClick={onClose} className="rounded-full px-5 py-2.5 text-sm hover:bg-brand-line/50">
                            Annuler
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="enroll-submit"
                            className="inline-flex items-center gap-2 rounded-full bg-brand-orange px-6 py-2.5 text-sm font-semibold text-white hover:bg-[#d9772c] disabled:opacity-60"
                        >
                            {submitting ? "Création…" : "Inscrire"}
                        </button>
                    </div>
                </form>
            </motion.div>
        </div>
    );
}

export default function AdminInscriptions() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ status: "", payment: "", q: "" });
    const [enrollOpen, setEnrollOpen] = useState(false);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const params = {};
            if (filters.status) params.status = filters.status;
            if (filters.payment) params.payment_status = filters.payment;
            const { data } = await api.get("/admin/enrollments", { params });
            const filtered = filters.q
                ? data.filter((e) =>
                      `${e.user?.first_name || ""} ${e.user?.last_name || ""} ${e.user?.email || ""} ${e.session?.formation_name || ""}`
                          .toLowerCase()
                          .includes(filters.q.toLowerCase())
                  )
                : data;
            setItems(filtered);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchItems();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.status, filters.payment]);

    const markAttended = async (id) => {
        if (!window.confirm("Marquer comme présent ? Un email d'attestation sera envoyé au stagiaire.")) return;
        try {
            await api.post(`/admin/enrollments/${id}/mark-attended`);
            toast.success("Marqué comme présent · attestation envoyée");
            fetchItems();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const sendConvention = async (id) => {
        try {
            await api.post(`/admin/enrollments/${id}/send-convention`);
            toast.success("Convention envoyée par email");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const exportCsv = async () => {
        try {
            const r = await api.get("/admin/inscriptions/export.csv", { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([r.data], { type: "text/csv;charset=utf-8" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `inscriptions-${new Date().toISOString().slice(0, 10)}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Export CSV téléchargé");
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    const cancelEnrollment = async (id) => {
        if (!window.confirm("Annuler cette inscription ?")) return;
        try {
            await api.delete(`/admin/enrollments/${id}`);
            toast.success("Inscription annulée");
            fetchItems();
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };

    return (
        <div className="mx-auto max-w-7xl px-6 py-10" data-testid="page-admin-inscriptions">
            <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
                <div>
                    <h1 className="font-display text-5xl font-normal tracking-tightest">Inscriptions</h1>
                    <p className="mt-2 text-brand-smoke">Gestion manuelle des inscriptions stagiaires aux sessions.</p>
                </div>
                <button
                    onClick={() => setEnrollOpen(true)}
                    data-testid="admin-enroll-btn"
                    className="inline-flex items-center justify-center gap-2 self-start rounded-full bg-brand-orange px-6 py-3 text-sm font-semibold text-white hover:bg-[#d9772c] md:self-auto"
                >
                    <UserPlus className="h-4 w-4" /> Inscrire un stagiaire
                </button>
            </div>

            <div className="mt-6 flex flex-wrap items-center gap-3">
                <button
                    onClick={exportCsv}
                    data-testid="inscriptions-export-csv"
                    className="inline-flex items-center gap-2 rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm font-semibold text-brand-ink hover:border-brand-orange hover:text-brand-orange"
                >
                    <Download className="h-4 w-4" /> Exporter CSV
                </button>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
                <div className="relative flex-1 min-w-[240px]">
                    <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-smoke" />
                    <input
                        placeholder="Rechercher par nom, email, formation…"
                        value={filters.q}
                        onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
                        onKeyDown={(e) => e.key === "Enter" && fetchItems()}
                        data-testid="inscriptions-search"
                        className="w-full rounded-full border border-brand-line bg-white py-2.5 pl-11 pr-4 text-sm outline-none focus:border-brand-orange"
                    />
                </div>
                <select value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} data-testid="inscriptions-filter-status" className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm">
                    <option value="">Tous statuts</option>
                    <option value="confirmed">Confirmé</option>
                    <option value="attended">Présent</option>
                    <option value="cancelled">Annulé</option>
                    <option value="no_show">Absent</option>
                </select>
                <select value={filters.payment} onChange={(e) => setFilters((f) => ({ ...f, payment: e.target.value }))} data-testid="inscriptions-filter-payment" className="rounded-full border border-brand-line bg-white px-5 py-2.5 text-sm">
                    <option value="">Tous paiements</option>
                    <option value="paid">Payé</option>
                    <option value="manual">Payé (manuel)</option>
                    <option value="pending">En attente</option>
                    <option value="refunded">Remboursé</option>
                </select>
                <button onClick={fetchItems} className="rounded-full border border-brand-line bg-white px-4 py-2.5 text-sm font-medium hover:border-brand-orange hover:text-brand-orange">
                    Actualiser
                </button>
            </div>

            <div className="mt-8 overflow-hidden rounded-2xl border border-brand-line bg-white shadow-premium" data-testid="admin-inscriptions-table">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-brand-line bg-[#FAFAFA] text-left text-xs font-semibold uppercase tracking-wider text-brand-smoke">
                                <th className="px-6 py-4">Stagiaire</th>
                                <th className="px-6 py-4">Session</th>
                                <th className="px-6 py-4">Date</th>
                                <th className="px-6 py-4">Statut</th>
                                <th className="px-6 py-4">Paiement</th>
                                <th className="px-6 py-4 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-brand-line">
                            {loading ? (
                                <tr><td colSpan={6} className="px-6 py-10 text-center text-brand-smoke">Chargement…</td></tr>
                            ) : items.length === 0 ? (
                                <tr><td colSpan={6} className="px-6 py-10 text-center text-brand-smoke">Aucune inscription.</td></tr>
                            ) : (
                                items.map((e) => (
                                    <tr key={e.id} className="hover:bg-[#FAFAFA]" data-testid={`inscription-row-${e.id}`}>
                                        <td className="px-6 py-4">
                                            <div className="font-medium text-brand-ink">{e.user?.first_name} {e.user?.last_name}</div>
                                            <div className="text-xs text-brand-smoke">{e.user?.email}</div>
                                        </td>
                                        <td className="px-6 py-4 text-brand-ink/85">{e.session?.formation_name}</td>
                                        <td className="px-6 py-4 text-brand-smoke">{formatDate(e.session?.start_date)} · {e.session?.city}</td>
                                        <td className="px-6 py-4">
                                            <Badge tone={e.status === "attended" ? "success" : e.status === "confirmed" ? "info" : "muted"}>
                                                {e.status === "attended" ? "Présent" : e.status === "confirmed" ? "Confirmé" : e.status === "cancelled" ? "Annulé" : "Absent"}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4">
                                            <Badge tone={e.payment_status === "paid" || e.payment_status === "manual" ? "success" : e.payment_status === "pending" ? "warning" : "muted"}>
                                                {e.payment_status === "paid" ? "Payé" : e.payment_status === "manual" ? "Payé (m.)" : e.payment_status === "pending" ? "Attente" : "Remboursé"}
                                            </Badge>
                                        </td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="inline-flex items-center gap-2">
                                                {e.status !== "attended" && (
                                                    <button
                                                        onClick={() => markAttended(e.id)}
                                                        title="Marquer comme présent (envoie l'attestation)"
                                                        data-testid={`inscription-mark-attended-${e.id}`}
                                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-green-600 hover:text-green-600"
                                                    >
                                                        <CheckCircle2 className="h-3.5 w-3.5" />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => sendConvention(e.id)}
                                                    title="Renvoyer la convention par email"
                                                    data-testid={`inscription-send-convention-${e.id}`}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-brand-orange hover:text-brand-orange"
                                                >
                                                    <Mail className="h-3.5 w-3.5" />
                                                </button>
                                                <button
                                                    onClick={() => cancelEnrollment(e.id)}
                                                    title="Annuler"
                                                    data-testid={`inscription-cancel-${e.id}`}
                                                    className="inline-flex h-8 w-8 items-center justify-center rounded-full border border-brand-line hover:border-red-300 hover:text-red-600"
                                                >
                                                    <Trash2 className="h-3.5 w-3.5" />
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            <EnrollDialog open={enrollOpen} onClose={() => setEnrollOpen(false)} onCreated={fetchItems} />
        </div>
    );
}
